import { useEffect, useState } from "react"

import { CAST_NAMESPACE, isCastReceiver } from "../cast/castConfig"
import type { CastRoomMessage } from "../cast/castConfig"
import type { CastFramework } from "../cast/castTypes"

const RECEIVER_SDK = "https://www.gstatic.com/cast/sdk/libs/caf_receiver/v3/cast_receiver_framework.js"

/**
 * The receiver context is a process-wide singleton and `start()` is not
 * idempotent — it opens the IPC channel to the Chromecast agent. React calling
 * an effect twice (StrictMode, or any remount) would open it twice, so the
 * booting is tracked here rather than in component state.
 */
let receiverStarted = false

/**
 * The television side of casting.
 *
 * A custom Cast receiver is just a web page the Chromecast loads — this page,
 * in fact — so all this does is bring up the receiver SDK and wait for the phone
 * to say which room to watch. Everything after that is the ordinary display
 * path: `useTvRoom` opens its own socket and watches like any other screen.
 *
 * `disableIdleTimeout` is the line that matters. CAF is built around media, and
 * a receiver that isn't playing any gets torn down after about five minutes of
 * "inactivity" — which for a game board is precisely the state it's supposed to
 * sit in while people argue about their answers.
 */

export type CastReceiverState =
    /** Not running on a Chromecast — the ordinary /tv page. */
    | { kind: "not-receiver" }
    /** On a Chromecast, waiting for a sender to name a room. */
    | { kind: "waiting" }
    | { kind: "room"; code: string }

export function useCastReceiver(): CastReceiverState {
    const [state, setState] = useState<CastReceiverState>(() =>
        isCastReceiver() ? { kind: "waiting" } : { kind: "not-receiver" })

    useEffect(() => {
        if (!isCastReceiver()) return

        let cancelled = false

        const boot = (framework: CastFramework) => {
            if (receiverStarted) return
            const ReceiverContext = framework.CastReceiverContext
            const ReceiverOptions = framework.CastReceiverOptions
            if (!ReceiverContext || !ReceiverOptions) return

            receiverStarted = true
            const context = ReceiverContext.getInstance()

            context.addCustomMessageListener(CAST_NAMESPACE, event => {
                const data = event.data as CastRoomMessage | string | undefined
                // Senders may deliver the payload as an object or as a JSON
                // string depending on how they were built; accept both rather
                // than being fussy at the one point the TV learns its room.
                const parsed = typeof data === "string"
                    ? (JSON.parse(data) as CastRoomMessage)
                    : data
                const code = parsed?.roomCode?.toUpperCase().replace(/[^A-Z0-9]/g, "")
                if (code && !cancelled) setState({ kind: "room", code })
            })

            const options = new ReceiverOptions()
            // See the note above: without this the board goes dark mid-game.
            options.disableIdleTimeout = true
            options.customNamespaces = framework.system
                ? { [CAST_NAMESPACE]: framework.system.MessageType.JSON }
                : undefined
            context.start(options)
        }

        if (window.cast?.framework) {
            boot(window.cast.framework)
            return () => { cancelled = true }
        }

        const script = document.createElement("script")
        script.src = RECEIVER_SDK
        script.async = true
        script.onload = () => { if (window.cast?.framework && !cancelled) boot(window.cast.framework) }
        // Nothing to fall back to on a Chromecast, but the page shouldn't throw:
        // it simply stays on "waiting", which is at least an honest screen.
        script.onerror = () => console.error("Cast: receiver SDK failed to load")
        document.head.appendChild(script)

        return () => { cancelled = true }
    }, [])

    return state
}
